/* Room-only wall continuation for the illustrative overview camera.
 * No image upload, pixel readback, third-party dependency or furniture duplication.
 * The source room is always drawn at the exact supplied background rectangle.
 */
(function (root, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.SeeGartRoomBackground = api;
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';
  // Two finished backgrounds are enough for responsive dragging without retaining
  // a full canvas for every reference-width value or every previously used room.
  const cache = [];
  function surface(width, height) {
    let out;
    if (typeof OffscreenCanvas === 'function') out = new OffscreenCanvas(width, height);
    else if (typeof document !== 'undefined') {
      out = document.createElement('canvas'); out.width = width; out.height = height;
    }
    return out || null;
  }
  function strip(image, sourceY, sourceHeight, imageWidth) {
    const out = surface(256, 1);
    if (!out) return null;
    const c = out.getContext('2d');
    c.imageSmoothingEnabled = true; c.imageSmoothingQuality = 'high';
    // Averaging a shallow, furniture-free strip retains broad lighting without
    // stretching individual plaster grains into visible vertical streaks.
    c.drawImage(image, 0, sourceY, imageWidth, sourceHeight, 0, 0, 256, 1);
    return out;
  }
  function spread(c, row, b, width, y, height) {
    c.drawImage(row, 0, 0, row.width, 1, b.x, y, b.width, height);
    if (b.x > 0) c.drawImage(row, 0, 0, 1, 1, 0, y, b.x + .5, height);
    const right = b.x + b.width;
    if (right < width) c.drawImage(row, row.width - 1, 0, 1, 1, right - .5, y, width - right + .5, height);
  }
  function compose(image, b, width, height, imageWidth, imageHeight) {
    const out = surface(width, height), scene = surface(width, height);
    if (!out || !scene) return null;
    const c = out.getContext('2d'), s = scene.getContext('2d');
    c.imageSmoothingEnabled = s.imageSmoothingEnabled = true;
    c.imageSmoothingQuality = s.imageSmoothingQuality = 'high';
    const band = Math.max(1, Math.min(16, imageHeight * .015));
    const wall = strip(image, 0, band, imageWidth);
    const floor = strip(image, Math.max(0, imageHeight - band), band, imageWidth);
    if (!wall || !floor) return null;
    spread(c, wall, b, width, 0, height);

    // Continue only color/light from the floor edge into the extra side margins.
    // Never magnify or repeat a sofa, plant, chair, console or other room object.
    const floorStart = Math.max(0, b.y + b.height * .75);
    const floorEnd = Math.max(floorStart + 1, b.y + b.height * .91);
    const bands = Math.max(1, Math.min(96, Math.ceil(height - floorStart)));
    const step = Math.max(0, height - floorStart) / bands;
    for (let i = 0; i < bands && step > 0; i++) {
      const y = floorStart + i * step;
      const q = Math.max(0, Math.min(1, (y + step / 2 - floorStart) / (floorEnd - floorStart)));
      c.globalAlpha = q * q * (3 - 2 * q);
      spread(c, floor, b, width, y, step + .5);
    }
    c.globalAlpha = 1;

    s.drawImage(image, b.x, b.y, b.width, b.height);
    s.globalCompositeOperation = 'destination-in';
    const side = Math.min(40, Math.max(12, b.width * .035));
    const leftFade = b.x > .5 ? side : 0, rightFade = b.x + b.width < width - .5 ? side : 0;
    const horizontal = s.createLinearGradient(b.x, 0, b.x + b.width, 0);
    horizontal.addColorStop(0, leftFade ? 'rgba(0,0,0,0)' : '#000');
    if (leftFade) horizontal.addColorStop(Math.min(.45, leftFade / b.width), '#000');
    if (rightFade) horizontal.addColorStop(Math.max(.55, 1 - rightFade / b.width), '#000');
    horizontal.addColorStop(1, rightFade ? 'rgba(0,0,0,0)' : '#000');
    s.fillStyle = horizontal; s.fillRect(0, 0, width, height);
    if (b.y > .5) {
      const top = s.createLinearGradient(0, b.y, 0, b.y + Math.min(28, b.height * .055));
      top.addColorStop(0, 'rgba(0,0,0,0)'); top.addColorStop(1, '#000');
      s.fillStyle = top; s.fillRect(0, 0, width, height);
    }
    c.drawImage(scene, 0, 0);
    return out;
  }
  function draw(ctx, image, backgroundRect, canvasWidth, canvasHeight) {
    const b = backgroundRect;
    const iw = image && (image.naturalWidth || image.width), ih = image && (image.naturalHeight || image.height);
    if (!ctx || !image || !b || ![b.x, b.y, b.width, b.height, canvasWidth, canvasHeight, iw, ih].every(Number.isFinite) || Math.min(b.width, b.height, canvasWidth, canvasHeight, iw, ih) <= 0) return false;
    const width = Math.round(canvasWidth), height = Math.round(canvasHeight);
    if (width < 1 || height < 1) return false;
    ctx.save();
    try {
      // Ordinary rooms are byte-for-byte the existing direct draw path: no mask,
      // recoloring, extension, offscreen allocation or change of perspective.
      if (Math.abs(b.x) < .01 && Math.abs(b.y) < .01 && Math.abs(b.width - width) < .01 && Math.abs(b.height - height) < .01) {
        ctx.drawImage(image, b.x, b.y, b.width, b.height); return true;
      }
      const key = [b.x, b.y, b.width, b.height, width, height, iw, ih].join('|');
      let entry = cache.find(v => v.image === image && v.key === key);
      if (!entry) {
        const bitmap = compose(image, b, width, height, iw, ih);
        if (!bitmap) { ctx.drawImage(image, b.x, b.y, b.width, b.height); return false; }
        entry = { image, key, bitmap }; cache.unshift(entry); if (cache.length > 2) cache.pop();
      }
      ctx.drawImage(entry.bitmap, 0, 0, width, height);
      return true;
    } finally { ctx.restore(); }
  }
  return { draw };
});
